import { useState, useCallback, useRef, useEffect } from 'react';
import config from 'src/config';

// Backend API endpoint (goes through your authenticated backend)
const API_BASE = `${config.apiBaseUrl}/vapi`;

export interface TranscriptMessage {
  id: string;
  role: 'assistant' | 'user';
  text: string;
  timestamp: Date;
  isFinal: boolean;
}

export interface SupplierCallState {
  supplierId: string;
  supplierName: string;
  status: 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';
  transcript: TranscriptMessage[];
  error?: string;
  callId?: string;
}

interface UseVapiCallReturn {
  supplierCalls: Map<string, SupplierCallState>;
  startCall: (supplier: { id: string; name: string; phone?: string }) => void;
  endCall: (supplierId: string) => void;
  endAllCalls: () => void;
  isAnyCallActive: boolean;
}

export function useVapiCall(): UseVapiCallReturn {
  const [supplierCalls, setSupplierCalls] = useState<
    Map<string, SupplierCallState>
  >(new Map());
  const callIdsRef = useRef<Map<string, string>>(new Map());
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map());
  const messageIdCounterRef = useRef(0);

  // Generate unique message ID
  const generateMessageId = useCallback(() => {
    messageIdCounterRef.current += 1;
    return `msg-${Date.now()}-${messageIdCounterRef.current}`;
  }, []);

  // Update a specific supplier's call state
  const updateSupplierCallState = useCallback(
    (
      supplierId: string,
      updates: Partial<Omit<SupplierCallState, 'supplierId' | 'supplierName'>>,
    ) => {
      setSupplierCalls((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(supplierId);
        if (existing) {
          newMap.set(supplierId, { ...existing, ...updates });
        }
        return newMap;
      });
    },
    [],
  );

  // Add a transcript message for a specific supplier
  // Logic: Find the message to update (if any) by looking at recent messages
  // - If last message is same role → update it
  // - If last message is different role, but there's a non-final same-role message after it → update that
  // - Otherwise → add new entry
  const addTranscriptMessage = useCallback(
    (
      supplierId: string,
      message: Omit<TranscriptMessage, 'id' | 'timestamp'>,
    ) => {
      // Skip empty messages
      const text = message.text.trim();
      if (!text) return;

      setSupplierCalls((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(supplierId);
        if (!existing) return prev;

        const updatedTranscript = [...existing.transcript];
        const lastIdx = updatedTranscript.length - 1;

        // Find which message to update (if any)
        let updateIdx = -1;

        if (lastIdx >= 0) {
          const lastMsg = updatedTranscript[lastIdx];

          if (lastMsg.role === message.role) {
            // Same role - only update if NOT final (progressive speech)
            // If final, add new entry (new sentence)
            if (!lastMsg.isFinal) {
              updateIdx = lastIdx;
            }
          } else {
            // Last message is different role - check if there's a non-final same-role message
            // This handles out-of-order delivery (partial from role A arrives after role B started)
            for (let i = lastIdx - 1; i >= 0; i--) {
              const msg = updatedTranscript[i];
              if (msg.role === message.role && !msg.isFinal) {
                updateIdx = i;
                break;
              }
              // Stop if we hit a final message from different role (that's a turn boundary)
              if (msg.role !== message.role && msg.isFinal) {
                break;
              }
            }
          }
        }

        if (updateIdx >= 0) {
          const existingMsg = updatedTranscript[updateIdx];
          const existingTextLower = existingMsg.text.trim().toLowerCase();
          const newTextLower = text.toLowerCase();

          // Skip exact duplicates
          if (existingTextLower === newTextLower) {
            if (message.isFinal && !existingMsg.isFinal) {
              updatedTranscript[updateIdx] = { ...existingMsg, isFinal: true };
              newMap.set(supplierId, {
                ...existing,
                transcript: updatedTranscript,
              });
              return newMap;
            }
            return prev;
          }

          // Replace with new text (progressive speech)
          updatedTranscript[updateIdx] = {
            ...existingMsg,
            text: text,
            isFinal: message.isFinal,
            timestamp: new Date(),
          };
        } else {
          // No message to update - add new entry
          updatedTranscript.push({
            ...message,
            text: text,
            id: generateMessageId(),
            timestamp: new Date(),
          });
        }

        newMap.set(supplierId, {
          ...existing,
          transcript: updatedTranscript,
        });
        return newMap;
      });
    },
    [generateMessageId],
  );

  // Mark all messages as final and clean up (call this when call ends)
  const finalizeTranscript = useCallback((supplierId: string) => {
    setSupplierCalls((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(supplierId);
      if (existing) {
        // Mark all messages as final to stop spinners
        const finalizedTranscript = existing.transcript.map((msg) => ({
          ...msg,
          isFinal: true,
        }));
        newMap.set(supplierId, {
          ...existing,
          transcript: finalizedTranscript,
        });
      }
      return newMap;
    });
  }, []);

  // Connect to SSE stream for live transcript updates
  const connectToStream = useCallback(
    (supplierId: string, callId: string) => {
      const eventSource = new EventSource(`${API_BASE}/call/${callId}/stream`, {
        withCredentials: true,
      });

      eventSourcesRef.current.set(supplierId, eventSource);

      eventSource.addEventListener('status', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📊 Status update:', data.status);

          if (data.status === 'queued' || data.status === 'connecting') {
            updateSupplierCallState(supplierId, { status: 'connecting' });
          } else if (data.status === 'ringing') {
            updateSupplierCallState(supplierId, { status: 'ringing' });
          } else if (data.status === 'in-progress' || data.status === 'active') {
            updateSupplierCallState(supplierId, { status: 'active' });
          } else if (data.status === 'ended' || data.status === 'completed') {
            updateSupplierCallState(supplierId, { status: 'ended' });
          }
        } catch (error) {
          console.error('Error parsing status event:', error);
        }
      });

      eventSource.addEventListener('transcript', (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📝 Transcript:', data.role, data.text);

          addTranscriptMessage(supplierId, {
            role: data.role as 'assistant' | 'user',
            text: data.text,
            isFinal: data.isFinal !== false,
          });
        } catch (error) {
          console.error('Error parsing transcript event:', error);
        }
      });

      eventSource.addEventListener('connected', (event) => {
        console.log('✅ SSE connected:', event.data);
      });

      eventSource.addEventListener('ended', (event) => {
        console.log('📞 Call ended:', event.data);
        finalizeTranscript(supplierId); // Mark all messages as final (stops spinners)
        updateSupplierCallState(supplierId, { status: 'ended' });
        eventSource.close();
        eventSourcesRef.current.delete(supplierId);
      });

      eventSource.addEventListener('error', (event) => {
        try {
          const messageEvent = event as MessageEvent;
          if (messageEvent.data) {
            const data = JSON.parse(messageEvent.data);
            console.error('❌ SSE error:', data.message || 'Unknown error');
            updateSupplierCallState(supplierId, {
              status: 'error',
              error: data.message || 'Connection error',
            });
          } else {
            // No data in error event - connection issue
            console.log('SSE error event (no data)');
          }
        } catch {
          // Generic error (connection lost, etc.)
          if (eventSource.readyState === EventSource.CLOSED) {
            console.log('SSE connection closed');
          }
        }
      });

      eventSource.onerror = () => {
        // Connection error - the call might have ended
        console.log('SSE connection error/closed');
      };
    },
    [updateSupplierCallState, addTranscriptMessage, finalizeTranscript],
  );

  // Start a call via the backend
  const startCall = useCallback(
    async (supplier: { id: string; name: string; phone?: string }) => {
      // Initialize call state
      setSupplierCalls((prev) => {
        const newMap = new Map(prev);
        newMap.set(supplier.id, {
          supplierId: supplier.id,
          supplierName: supplier.name,
          status: 'connecting',
          transcript: [],
        });
        return newMap;
      });

      try {
        // Call the backend to start the Vapi call
        const response = await fetch(`${API_BASE}/call/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            supplierName: supplier.name,
            phoneNumber: supplier.phone,
          }),
        });

        // Get response text first to handle empty responses
        const responseText = await response.text();
        console.log(
          '📡 Response status:',
          response.status,
          responseText.substring(0, 200),
        );

        if (!response.ok) {
          let errorMessage = `Server error: ${response.status}`;
          try {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorMessage;
          } catch {
            // Response wasn't JSON
            errorMessage = responseText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const data = JSON.parse(responseText);
        const callId = data.callId;

        console.log('📞 Call started:', callId);
        callIdsRef.current.set(supplier.id, callId);

        updateSupplierCallState(supplier.id, {
          status: 'ringing',
          callId,
        });

        // Add initial message
        addTranscriptMessage(supplier.id, {
          role: 'assistant',
          text: `📞 Calling supplier...`,
          isFinal: true,
        });

        // Connect to SSE stream for live updates
        connectToStream(supplier.id, callId);
      } catch (error) {
        console.error('Failed to start call:', error);
        updateSupplierCallState(supplier.id, {
          status: 'error',
          error:
            error instanceof Error ? error.message : 'Failed to start call',
        });
      }
    },
    [updateSupplierCallState, addTranscriptMessage, connectToStream],
  );

  // End a specific call
  const endCall = useCallback(
    async (supplierId: string) => {
      const callId = callIdsRef.current.get(supplierId);

      // Close EventSource
      const eventSource = eventSourcesRef.current.get(supplierId);
      if (eventSource) {
        eventSource.close();
        eventSourcesRef.current.delete(supplierId);
      }

      // End call via backend
      if (callId) {
        try {
          await fetch(`${API_BASE}/call/${callId}/end`, {
            method: 'POST',
            credentials: 'include',
          });
        } catch (error) {
          console.error('Error ending call:', error);
        }
        callIdsRef.current.delete(supplierId);
      }

      finalizeTranscript(supplierId); // Mark all messages as final (stops spinners)
      updateSupplierCallState(supplierId, { status: 'ended' });
    },
    [updateSupplierCallState, finalizeTranscript],
  );

  // End all active calls
  const endAllCalls = useCallback(() => {
    for (const supplierId of callIdsRef.current.keys()) {
      endCall(supplierId);
    }
  }, [endCall]);

  // Check if any call is active
  const isAnyCallActive = Array.from(supplierCalls.values()).some(
    (call) =>
      call.status === 'active' ||
      call.status === 'connecting' ||
      call.status === 'ringing',
  );

  // Cleanup on unmount
  useEffect(() => {
    const eventSources = eventSourcesRef.current;
    const callIds = callIdsRef.current;

    return () => {
      // Close all EventSources
      for (const eventSource of eventSources.values()) {
        eventSource.close();
      }
      eventSources.clear();

      // End all calls
      for (const callId of callIds.values()) {
        fetch(`${API_BASE}/call/${callId}/end`, {
          method: 'POST',
          credentials: 'include',
        }).catch(console.error);
      }
      callIds.clear();
    };
  }, []);

  return {
    supplierCalls,
    startCall,
    endCall,
    endAllCalls,
    isAnyCallActive,
  };
}
